import api from "./api";

// API Response Types (matching actual backend structure)
export interface ApiSunbedRoom {
  label: string;
  status: string | null;
  badge: string;
}

export interface ApiSunbedPricing {
  label: string;
  type: string; // e.g., "Per min", "Fixed"
  price: string;
  badge: string;
}

export interface ApiSunbed {
  id: number;
  label: string;
  status: string;
  rooms: ApiSunbedRoom[];
  pricing: ApiSunbedPricing[];
  total_hours: string;
  total_revenue: string;
}

export interface ApiSunbedsResponse {
  status: "success" | "error";
  data: ApiSunbed[];
}

// Internal Types (for component usage)
export interface SunbedPricing {
  id: number | string;
  type: "fixed" | "per_minute";
  value: string;
  price: number | string;
  label: string;
}

export interface Sunbed {
  id: number | string;
  name: string;
  description?: string | null;
  company_id?: number;
  location_id?: number | null;
  status?: string;
  pricing: SunbedPricing[];
  rooms?: ApiSunbedRoom[];
  image?: string | null;
  image_url?: string | null;
}

/**
 * Convert API pricing type to internal type
 */
const normalizePricingType = (type: string): "fixed" | "per_minute" => {
  const lowerType = type.toLowerCase();
  if (lowerType.includes("per min") || lowerType.includes("per_minute") || lowerType.includes("per minute")) {
    return "per_minute";
  }
  return "fixed";
};

/**
 * Extract minutes from pricing label/value for fixed pricing
 */
const extractMinutesFromLabel = (label: string): number => {
  const match = label.match(/(\d+)\s*(?:min|minute|mins|minutes)/i);
  return match ? parseInt(match[1]) : 0;
};

/**
 * Convert API sunbed to internal Sunbed format
 */
const mapApiSunbedToSunbed = (apiSunbed: ApiSunbed, index: number): Sunbed => {
  // Map pricing from API format to internal format
  const mappedPricing: SunbedPricing[] = apiSunbed.pricing.map((pricing, idx) => {
    const pricingType = normalizePricingType(pricing.type);
    const priceValue = parseFloat(pricing.price) || 0;

    // For fixed pricing, try to extract minutes from label or use default
    // For per_minute pricing, use "1" as the value (1 unit per minute)
    let value: string;
    if (pricingType === "fixed") {
      const minutes = extractMinutesFromLabel(pricing.label);
      value = minutes > 0 ? `${minutes} mins` : pricing.label;
    } else {
      // per_minute - use "1" as the base unit
      value = "1";
    }

    return {
      id: `${apiSunbed.id}-pricing-${idx}`, // Generate ID if not provided
      type: pricingType,
      value: value,
      price: priceValue,
      label: pricing.label,
    };
  });

  return {
    id: apiSunbed.id,
    name: apiSunbed.label,
    status: apiSunbed.status,
    pricing: mappedPricing,
    rooms: apiSunbed.rooms,
    company_id: undefined, // Not in API response, will be set from user context
  };
};

/**
 * Fetch all sunbeds for the current company
 */
export const getSunbeds = async (): Promise<Sunbed[]> => {
  try {
    const response = await api.get<ApiSunbedsResponse>("/admin/sunbeds");

    console.log("Sunbeds API Full Response:", response);
    console.log("Sunbeds API Response Data:", response.data);
    console.log("Response Status:", response.data?.status);
    console.log("Response Data Array:", response.data?.data);

    // Check if response is successful
    if (!response.data) {
      console.warn("No response data received");
      return [];
    }

    if (response.data.status !== "success") {
      console.warn("Response status is not 'success':", response.data.status);
      return [];
    }

    if (!Array.isArray(response.data.data)) {
      console.warn("Response data is not an array:", typeof response.data.data, response.data.data);
      return [];
    }

    if (response.data.data.length === 0) {
      console.log("No sunbeds returned from API");
      return [];
    }

    // Map API response to internal format
    const sunbeds = response.data.data.map(mapApiSunbedToSunbed);

    console.log("Mapped sunbeds:", sunbeds);
    console.log("Number of sunbeds mapped:", sunbeds.length);

    return sunbeds;
  } catch (err: any) {
    console.error("Error fetching sunbeds:", err);
    console.error("Error details:", {
      message: err?.message,
      response: err?.response?.data,
      status: err?.response?.status,
    });

    if (err?.response?.status === 404) {
      // Endpoint might not exist yet, return empty array
      console.warn("Sunbeds endpoint not found, returning empty array");
      return [];
    }

    throw {
      type: "network",
      message: err.response?.data?.message || "Failed to fetch sunbeds",
    };
  }
};

/**
 * Fetch a single sunbed by ID
 */
export const getSunbed = async (id: number | string): Promise<Sunbed> => {
  try {
    const response = await api.get<{ status: string; data: ApiSunbed }>(`/admin/sunbeds/${id}`);

    if (response.data?.status !== "success" || !response.data.data) {
      throw new Error("Invalid sunbed response");
    }

    return mapApiSunbedToSunbed(response.data.data, 0);
  } catch (err: any) {
    console.error(`Error fetching sunbed ${id}:`, err);
    throw {
      type: "network",
      message: err.response?.data?.message || "Failed to fetch sunbed",
    };
  }
};
